import { useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./InfoTooltip.css";

export default function InfoTooltip({ text, label = "Informatie" }) {
  const id = useId();
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [position, setPosition] = useState({ top: -1000, left: -1000 });
  const visible = hovered || focused;

  useLayoutEffect(() => {
    if (!visible) return undefined;

    const updatePosition = () => {
      const trigger = triggerRef.current;
      const tooltip = tooltipRef.current;
      if (!trigger || !tooltip) return;

      const rect = trigger.getBoundingClientRect();
      const width = tooltip.offsetWidth;
      const height = tooltip.offsetHeight;
      const gap = 8;
      const edge = 8;
      const top = window.innerHeight - rect.bottom >= height + gap + edge
        ? rect.bottom + gap
        : Math.max(edge, rect.top - height - gap);
      const left = Math.max(
        edge,
        Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - edge)
      );
      setPosition({ top, left });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [visible, text]);

  if (!text) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="info-tooltip__trigger"
        aria-label={label}
        aria-describedby={visible ? id : undefined}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onClick={() => setFocused(true)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setHovered(false);
            setFocused(false);
            event.currentTarget.blur();
          }
        }}
      >
        <img src="/help-circle.png" alt="" aria-hidden="true" />
      </button>
      {visible && createPortal(
        <div
          ref={tooltipRef}
          id={id}
          role="tooltip"
          className="info-tooltip__bubble"
          style={position}
        >
          {text}
        </div>,
        document.body
      )}
    </>
  );
}
